import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Bell, BellOff, ChevronLeft, CornerUpLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationList, Message, MessagePage } from '@up2d8/contracts';
import { Button, Card, EmptyState, LoadingState, Textarea, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskConversation } from './desk/desk-conversation';
import { DeskConversationInfo } from './desk/desk-conversation-info';
import { DeskMessagesPage } from './desk/desk-messages-page';
import { DeskShell } from './desk/desk-shell';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';
import { uiLocale } from '../../shared/format';
import {
  ReactionPicker,
  ReactionPills,
  ReplyComposerBar,
  ReplyQuote,
  jumpToMessage,
  useReactToMessage,
} from './chat-message-extras';

/**
 * Eén gesprek, in twee indelingen (Stap 34c; het bureauscherm sinds BER-6).
 *
 * Op een bureauscherm is dit hetzelfde drieluik als de gesprekkenlijst, met dít gesprek in paneel
 * 2 — je blijft dus in het scherm waar je was, met de lijst ernaast. Daaronder blijft het één
 * scherm met een terugpijl.
 *
 * Nieuwste onderaan, zoals een gesprek leest — maar de server levert nieuwste eerst, dus de lijst
 * wordt gekeerd. Dat is geen omslachtigheid: terugbladeren gaat de tijd in, en dan is "nieuwste
 * eerst" de goedkoopste query.
 *
 * Geen aanwezigheid en geen typ-indicatie (ADR-0050). De leesbevestiging is er sinds ADR-0068 wél,
 * onder je eigen bericht en alleen in een groep.
 */
export function AppConversationPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { conversationId } = useParams({ strict: false }) as { conversationId: string };
  const tabs = useAppTabs();

  /*
    Of het infopaneel open is, is een keuze van de lezer en hoort die sessie te overleven (§7).

    `sessionStorage` en niet `localStorage`: het is een voorkeur van dit bezoek, niet van dit
    toestel — en op een gedeelde terminal in de fabriek hoort de keuze van de vorige ploeg niet te
    blijven staan. Een browser die opslag weigert, mag het scherm niet breken.
  */
  const [infoOpen, setInfoOpen] = useState(() => {
    try {
      return window.sessionStorage.getItem('up2d8.chat.info') !== 'dicht';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      window.sessionStorage.setItem('up2d8.chat.info', infoOpen ? 'open' : 'dicht');
    } catch {
      // Zie hierboven.
    }
  }, [infoOpen]);

  /** Het zoekveld in de gesprekskop; ook de actie "Zoeken" in het infopaneel zet het aan. */
  const [search, setSearch] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  /** Het bericht waarop je antwoordt (BER-8). */
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  /**
   * Bij welk bericht de acties openstaan. Een telefoon kent geen aanwijzen: een tik op de ballon
   * toont de emoji's en "Antwoorden", een tweede tik verbergt ze.
   */
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  const react = useReactToMessage(conversationId);

  // Een ander gesprek openen laat niets van het vorige meelopen: ook de half getypte zin en de
  // zoekterm niet, anders gaat "Versturen" met de tekst voor de ene collega naar de andere.
  useEffect(() => {
    setReplyTo(null);
    setActionsFor(null);
    setDraft('');
    setSearch(null);
  }, [conversationId]);

  const conversations = useQuery({
    queryKey: ['conversations'],
    queryFn: async (): Promise<ConversationList> => {
      const { data, error } = await api.GET('/api/v1/conversations');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ConversationList;
    },
  });
  // Elk id van een samengevoegd gesprek opent dezelfde rij (CHT-1): een melding wijst naar het
  // gesprek waarin het bericht stond, de lijst naar het gesprek vooraan.
  const conversation = conversations.data?.items.find(
    (item) => item.id === conversationId || item.sourceIds.includes(conversationId),
  );

  const messages = useQuery({
    queryKey: ['messages', conversationId],
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<MessagePage> => {
      const { data, error } = await api.GET('/api/v1/conversations/{conversationId}/messages', {
        params: { path: { conversationId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as MessagePage;
    },
  });

  /**
   * Het gesprek openen zet de teller op nul.
   *
   * Bij het openen en niet bij het eerste bericht dat in beeld komt: een gesprek met drie berichten
   * lees je in één blik, en een teller die blijft staan tot je naar beneden scrolt, is een teller
   * die liegt.
   */
  const markRead = useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/conversations/{conversationId}/read', {
        params: { path: { conversationId } },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  /**
   * Markeren bij het openen, en opnieuw wanneer er een bericht van een ander binnenkomt terwijl
   * het gesprek openstaat — anders blijft dat bericht ongelezen tot je het gesprek verlaat.
   *
   * De mutatie via een ref en niet in de afhankelijkheidslijst: die verandert bij elke render van
   * identiteit en zou een schrijfactie per render geven. `markedRef` houdt bij voor welk gesprek
   * en welk laatste binnengekomen bericht al gemarkeerd is.
   */
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const markedRef = useRef<string | null>(null);
  const seenIncomingRef = useRef<{ conversationId: string; id: string | null | undefined } | null>(
    null,
  );
  const unread = conversation?.unreadCount ?? 0;
  // Nieuwste eerst van de server; `undefined` zolang de berichten nog niet geladen zijn.
  const latestIncoming = messages.data
    ? (messages.data.items.find((message) => !message.mine)?.id ?? null)
    : undefined;

  useEffect(() => {
    const previous = seenIncomingRef.current;
    seenIncomingRef.current = { conversationId, id: latestIncoming };
    // Alleen een bericht dat binnenkwam terwijl het gesprek al geladen openstond.
    const fresh =
      previous?.conversationId === conversationId &&
      previous.id !== undefined &&
      latestIncoming != null &&
      previous.id !== latestIncoming;
    const key = `${conversationId}:${latestIncoming ?? ''}`;
    const openedUnread = unread > 0 && !markedRef.current?.startsWith(`${conversationId}:`);
    if (openedUnread || (fresh && markedRef.current !== key)) {
      markedRef.current = key;
      markReadRef.current.mutate();
    }
  }, [conversationId, unread, latestIncoming]);

  const send = useMutation({
    mutationFn: async (body: string) => {
      const { error } = await api.POST('/api/v1/conversations/{conversationId}/messages', {
        params: { path: { conversationId } },
        // Bijlagen kunnen sinds BER-2, maar de knop ernaartoe komt pas met het bureauscherm
        // (BER-6). Tot dan stuurt dit scherm een lege lijst mee.
        body: { body, attachmentFileIds: [], replyToId: replyTo?.id ?? null },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setDraft('');
      setReplyTo(null);
      void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      // Geen offline-wachtrij: een bericht dat drie dagen later opduikt onder een gesprek dat al
      // verder is, is erger dan een bericht dat niet verstuurd werd (ADR-0050).
      toast.show({ tone: 'danger', title: t('messaging.sendFailed') });
    },
  });

  const mute = useMutation({
    mutationFn: async (muted: boolean) => {
      const { error } = await api.POST('/api/v1/conversations/{conversationId}/mute', {
        params: { path: { conversationId } },
        body: { muted },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  // Nieuwste onderaan: de server levert nieuwste eerst, het scherm leest andersom.
  const items = [...(messages.data?.items ?? [])].reverse();
  const trimmed = draft.trim();

  return (
    <DeskShell
      fill
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="messages"
      renderLink={renderAppTabLink}
      messageCount={conversations.data?.totalUnread ?? null}
    >
      {/* --- Bureauscherm: hetzelfde drieluik, met dít gesprek in paneel 2 --- */}
      <div className="hidden min-h-0 desk:block desk:h-full">
        <DeskMessagesPage
          activeId={conversation?.id ?? conversationId}
          info={
            conversation && infoOpen ? (
              <DeskConversationInfo
                conversation={conversation}
                onClose={() => setInfoOpen(false)}
                onSearch={() => setSearch('')}
              />
            ) : undefined
          }
          conversation={
            conversation ? (
              <DeskConversation
                conversation={conversation}
                infoOpen={infoOpen}
                onToggleInfo={() => setInfoOpen((open) => !open)}
                search={search}
                onSearchChange={setSearch}
              />
            ) : undefined
          }
        />
      </div>

      {/* --- Telefoon: één scherm met een weg terug --- */}
      <div className="flex flex-col gap-element desk:hidden">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            data-touch-target
            onClick={() => void navigate({ to: '/app/gesprekken' })}
            className="inline-flex min-h-touch items-center gap-1 rounded-pill px-2 text-small text-text-secondary hover:text-brand-primary"
          >
            <ChevronLeft aria-hidden className="h-4 w-4" />
            {t('messaging.title')}
          </button>
          {conversation && (
            <button
              type="button"
              data-touch-target
              data-testid="mute-conversation"
              aria-pressed={conversation.muted}
              aria-label={conversation.muted ? t('messaging.unmute') : t('messaging.mute')}
              onClick={() => mute.mutate(!conversation.muted)}
              className="inline-flex min-h-touch items-center justify-center rounded-pill p-2 text-text-secondary hover:bg-surface hover:text-brand-primary"
            >
              {conversation.muted ? (
                <BellOff aria-hidden className="h-5 w-5" />
              ) : (
                <Bell aria-hidden className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        <h1 className="font-heading text-page-title font-semibold text-text-primary">
          {conversation?.title ?? t('messaging.title')}
        </h1>
        {messages.isPending ? (
          <LoadingState rows={4} label={t('common.loading')} />
        ) : items.length === 0 ? (
          <Card>
            <EmptyState
              title={t('messaging.noMessages')}
              description={t('messaging.noMessagesHint')}
            />
          </Card>
        ) : (
          <ul ref={list} className="flex flex-col gap-2" data-testid="message-list">
            {items.map((message) =>
              // Een systeemregel (iemand toegevoegd of verwijderd) heeft geen tekst en geen
              // afzender-ballon: een gecentreerde regel, zoals op het bureauscherm.
              message.kind === 'system' ? (
                <li
                  key={message.id}
                  data-testid="chat-system"
                  data-message-id={message.id}
                  className="text-center text-caption text-text-muted"
                >
                  {message.systemEvent === 'participant_added'
                    ? t('messaging.systemAdded', {
                        actor: message.senderDisplayName,
                        person: message.systemAboutName ?? '',
                      })
                    : t('messaging.systemRemoved', {
                        actor: message.senderDisplayName,
                        person: message.systemAboutName ?? '',
                      })}
                </li>
              ) : (
                <li
                  key={message.id}
                  data-testid="message"
                  data-message-id={message.id}
                  data-mine={message.mine}
                  className={cn('flex flex-col', message.mine ? 'items-end' : 'items-start')}
                >
                  <div
                    role={message.kind === 'text' ? 'button' : undefined}
                    tabIndex={message.kind === 'text' ? 0 : undefined}
                    aria-expanded={message.kind === 'text' ? actionsFor === message.id : undefined}
                    onClick={(event) => {
                      if (message.kind !== 'text') return;
                      // Een tik op het citaat springt; dat is geen tik om de acties te openen.
                      if ((event.target as HTMLElement).closest('[data-testid="chat-reply-quote"]'))
                        return;
                      setActionsFor((open) => (open === message.id ? null : message.id));
                    }}
                    onKeyDown={(event) => {
                      if (message.kind === 'text' && event.key === 'Enter') {
                        setActionsFor((open) => (open === message.id ? null : message.id));
                      }
                    }}
                    className={cn(
                      'max-w-[85%] rounded-lg px-3 py-2',
                      message.mine
                        ? 'bg-brand-primary-tint text-text-primary'
                        : 'bg-surface-sunk text-text-primary',
                    )}
                  >
                    {message.replyTo && (
                      <ReplyQuote
                        reply={message.replyTo}
                        mine={false}
                        onJump={(id) => jumpToMessage(list.current, id)}
                      />
                    )}
                    {/* Platte tekst, nooit HTML: wat een medewerker typt, wordt geen opmaak. */}
                    <p className="whitespace-pre-wrap break-words text-content">{message.body}</p>
                    <p className="mt-0.5 text-right text-caption text-text-muted">
                      {new Date(message.createdAt).toLocaleTimeString(uiLocale(), {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>

                  <ReactionPills
                    message={message}
                    onReact={(emoji) => react.mutate({ messageId: message.id, emoji })}
                    className="mt-1"
                  />

                  {actionsFor === message.id && (
                    <div
                      data-testid="message-actions"
                      className={cn(
                        'mt-1 flex flex-wrap items-center gap-1.5',
                        message.mine && 'justify-end',
                      )}
                    >
                      <ReactionPicker
                        current={message.myReaction}
                        onPick={(emoji) => {
                          setActionsFor(null);
                          react.mutate({
                            messageId: message.id,
                            emoji: message.myReaction === emoji ? null : emoji,
                          });
                        }}
                      />
                      <button
                        type="button"
                        data-touch-target
                        data-testid="message-reply"
                        onClick={() => {
                          setReplyTo(message);
                          setActionsFor(null);
                          document.getElementById('message-body')?.focus();
                        }}
                        className="inline-flex min-h-touch items-center gap-1.5 rounded-pill border border-border-light bg-surface px-3 text-small text-text-secondary shadow-sm hover:text-brand-primary"
                      >
                        <CornerUpLeft aria-hidden className="h-4 w-4" />
                        {t('messaging.reply')}
                      </button>
                    </div>
                  )}
                </li>
              ),
            )}
          </ul>
        )}

        <form
          className="flex flex-col gap-element"
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed.length > 0) send.mutate(trimmed);
          }}
        >
          {replyTo && <ReplyComposerBar message={replyTo} onCancel={() => setReplyTo(null)} />}
          <Textarea
            id="message-body"
            label={t('messaging.messageLabel')}
            placeholder={t('messaging.messagePlaceholder')}
            value={draft}
            maxLength={2000}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button
            type="submit"
            data-testid="send-message"
            loading={send.isPending}
            disabled={trimmed.length === 0}
          >
            {t('messaging.send')}
          </Button>
        </form>
      </div>
    </DeskShell>
  );
}
